import React from 'react';
import { useTranslation } from 'react-i18next';
import { KoobTooltip, tableActionVariants } from '@koob/ui';
import { formatDateTime } from '@koob/dates';

export interface FolderTravelDiaryActionProps {
  /** The app's own diary page; it opens in a new tab. */
  href: string;
  viewCount?: number | null;
  firstViewedAt?: string | null;
  lastViewedAt?: string | null;
}

const DATE_TIME = 'DD/MM/YYYY HH:mm';

/**
 * KD-3842: open-book icon opening the trip travel diary in a new tab, with a
 * green badge counting the client's views (trips.diary_view_count) and their
 * first and last date in the tooltip.
 */
export default function FolderTravelDiaryAction({
  href,
  viewCount,
  firstViewedAt,
  lastViewedAt,
}: FolderTravelDiaryActionProps) {
  const { t } = useTranslation('folders');
  const count = viewCount ?? 0;

  const tip = (
    <span className="flex flex-col gap-0.5 text-left">
      <span className="font-semibold">{t('travelDiary.open')}</span>
      {count > 0 ? (
        <>
          <span>{t('travelDiary.viewed', { count })}</span>
          {firstViewedAt && (
            <span className="opacity-80">
              {t('travelDiary.firstViewed', {
                date: formatDateTime(firstViewedAt, DATE_TIME),
              })}
            </span>
          )}
          {lastViewedAt && (
            <span className="opacity-80">
              {t('travelDiary.lastViewed', {
                date: formatDateTime(lastViewedAt, DATE_TIME),
              })}
            </span>
          )}
        </>
      ) : (
        <span className="opacity-80">{t('travelDiary.notViewed')}</span>
      )}
    </span>
  );

  return (
    <KoobTooltip tip={tip}>
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={t('travelDiary.open')}
        className={`relative ${tableActionVariants()}`}
        onClick={(event) => event.stopPropagation()}
        data-testid="folder-trip-travel-diary"
      >
        <i className="fa-regular fa-book-open" aria-hidden="true" />
        {count > 0 && (
          <span
            className="pointer-events-none absolute -right-1 -top-1 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-green-600 px-1 text-[10px] font-semibold leading-none text-white"
            data-testid="folder-trip-travel-diary-count"
          >
            {count}
          </span>
        )}
      </a>
    </KoobTooltip>
  );
}
